export const DEFAULT_SWIMLANE_ID = "unassigned";

const RESERVED_SWIMLANE_IDS = new Set([
	DEFAULT_SWIMLANE_ID,
	"new",
	"toReview",
	"waitingOnAuthor",
	"reviewed",
	"tracking",
	"merged",
	"deleted",
	"hidden",
]);

export const normalizeCustomSwimlanes = (lanes) => {
	if (!Array.isArray(lanes)) return [];

	const seenIds = new Set();
	return lanes.reduce((normalized, lane) => {
		const id = typeof lane?.id === "string" ? lane.id.trim() : "";
		const title = typeof lane?.title === "string" ? lane.title.trim() : "";
		if (!id || !title || RESERVED_SWIMLANE_IDS.has(id) || seenIds.has(id)) {
			return normalized;
		}

		seenIds.add(id);
		normalized.push({ id, title });
		return normalized;
	}, []);
};

export const createCustomSwimlane = (title, id) => {
	const normalizedTitle = title.trim();
	if (!normalizedTitle || !id || RESERVED_SWIMLANE_IDS.has(id)) return null;
	return { id, title: normalizedTitle };
};

export const reorderSwimlanes = (
	orderedIds,
	movingId,
	targetId,
	edge = "before",
) => {
	if (movingId === targetId || !orderedIds.includes(movingId))
		return orderedIds;
	const reordered = orderedIds.filter((id) => id !== movingId);
	const targetIndex = reordered.indexOf(targetId);
	if (targetIndex === -1) return orderedIds;
	reordered.splice(
		edge === "after" ? targetIndex + 1 : targetIndex,
		0,
		movingId,
	);
	return reordered;
};

export const assignmentColumn = (assignment) =>
	typeof assignment === "object" && assignment !== null
		? assignment.lane
		: assignment;

export const assignmentSwimlane = (assignment) =>
	typeof assignment === "object" && assignment !== null
		? assignment.swimlane || DEFAULT_SWIMLANE_ID
		: DEFAULT_SWIMLANE_ID;

export const assignmentPosition = (assignment) =>
	typeof assignment === "object" && assignment !== null
		? assignment.position || 0
		: 0;

export const moveSwimlaneAssignments = (
	assignments,
	fromSwimlaneId,
	targetSwimlaneId = DEFAULT_SWIMLANE_ID,
) => {
	const nextAssignments = { ...assignments };
	const nextPositions = new Map();

	for (const assignment of Object.values(assignments)) {
		if (assignmentSwimlane(assignment) !== targetSwimlaneId) continue;
		const column = assignmentColumn(assignment);
		const nextPosition = Math.max(
			nextPositions.get(column) || 0,
			assignmentPosition(assignment) + 1,
		);
		nextPositions.set(column, nextPosition);
	}

	const assignmentsToMove = Object.entries(assignments)
		.filter(
			([, assignment]) => assignmentSwimlane(assignment) === fromSwimlaneId,
		)
		.sort(
			([, first], [, second]) =>
				assignmentPosition(first) - assignmentPosition(second),
		);

	for (const [mrId, assignment] of assignmentsToMove) {
		const metadata =
			typeof assignment === "object" && assignment !== null ? assignment : {};
		const column = assignmentColumn(assignment) || "new";
		const position = nextPositions.get(column) || 0;
		nextAssignments[mrId] = {
			...metadata,
			lane: column,
			swimlane: targetSwimlaneId,
			position,
		};
		nextPositions.set(column, position + 1);
	}

	return nextAssignments;
};
